import { PaginatedResponse, ServiceArea } from "@/api.types";
import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

type Nullable<T> = T | null;

export const useServiceAreaByDepotId = (depotId?: Nullable<string>) => {
  const { token } = useSession();
  return useQuery({
    queryKey: ["serviceAreas", depotId],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<ServiceArea>>(
        `${import.meta.env.VITE_API_URL}/api/v1/depots/${depotId}/serviceAreas`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!depotId,
  });
};

export type OperatingWindow = z.infer<typeof operatingWindowSchema>;
export const operatingWindowSchema = z.object({
  type: z.enum(["PICKUP", "DELIVERY"]).default("PICKUP"),
  daysOfWeek: z.array(z.number().min(1).max(7)),
  startTime: z.string(),
  endTime: z.string(),
  capacity: z.number().min(0),
  isActive: z.boolean().default(true),
});

export type CreateServiceAreaFormData = z.infer<typeof createServiceAreaSchema>;
export const createServiceAreaSchema = z.object({
  code: z.string().nonempty("Code is required"),
  name: z.string().nonempty("Name is required"),
  isActive: z.boolean().default(true),
  boundary: z
    .object({
      type: z.string(),
      coordinates: z.array(z.array(z.array(z.number()))),
    })
    .describe("GeoJSON polygon"),
  postcodes: z.array(z.string()),
  metadata: z.record(z.any()).nullable(),
  depotId: z.string().nonempty("Depot is required"),
  operatingWindows: z.array(operatingWindowSchema),
});

export const useCreateServiceArea = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: CreateServiceAreaFormData) => {
      const { data } = await axios.post<ServiceArea>(
        `${import.meta.env.VITE_API_URL}/api/v1/depots/${values.depotId}/serviceAreas/create`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["serviceAreas"] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error("Error creating service area:", error);
      onError?.(error);
    },
  });
};

export const useUpdateServiceArea = ({
  onSuccess,
}: {
  onSuccess: () => void;
}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: CreateServiceAreaFormData & { id: string }) => {
      const { data } = await axios.patch<ServiceArea>(
        `${import.meta.env.VITE_API_URL}/api/v1/depots/${values.depotId}/serviceAreas/${values.id}`,
        values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["serviceAreas"] });
      onSuccess();
    },
    onError: (error) => {
      console.error("Error updating service area:", error);
    },
  });
};

export const useServiceAreas = () => {
  const { token } = useSession();
  return useQuery({
    queryKey: ["serviceAreas"],
    queryFn: async () => {
      const { data } = await axios.get<ServiceArea[]>(
        `${import.meta.env.VITE_API_URL}/api/v1/serviceAreas`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!token,
  });
};
